import './TimeSlots.css'
import { useCallback, useState, type ReactNode } from 'react'
import { ChipBox } from '@/components/scalar/ChipBox'
import { TextBox } from '@/components/scalar/TextBox'
import { IconButton } from '@/components/action/IconButton'
import { toastError, toastSuccess } from '@/components/action/Toaster'

//--------------------------------------------------------------------------------
export type TimeSlotsProps = {
  /** Comma-separated HHMMSS integers (same format as saved on the feed). */
  value: string
  onChange: (next: string) => void
  disabled?: boolean
  label?: string
  tooltip?: ReactNode
  /** Width for the “add slot” field (e.g. `10rem` or `100%`). */
  addFieldWidth?: string | number
}

export function TimeSlots({
  value,
  onChange,
  disabled,
  label = 'Time slots',
  tooltip,
  addFieldWidth = '10rem',
}: TimeSlotsProps) {
  const [newSlot, setNewSlot] = useState('')

  const handleAdd = useCallback(() => {
    const trimmed = newSlot.trim()
    if (!trimmed) return

    const parts = trimmed.split(',').map((s) => s.trim()).filter(Boolean)
    const valid: number[] = []
    const invalid: string[] = []
    for (const part of parts) {
      const parsed = parseTimeSlot(part)
      if (parsed === null) invalid.push(part)
      else valid.push(parsed)
    }

    if (invalid.length > 0) {
      toastError(`Invalid time(s): ${invalid.join(', ')}`)
      return
    }

    const existingSet = new Set(
      value.split(',').map((s) => s.trim()).filter(Boolean).map((s) => parseInt(s, 10)),
    )
    const newOnes = [...new Set(valid)].filter((n) => !existingSet.has(n))

    if (newOnes.length === 0) {
      toastError('No new time slots to add (already in list)')
      return
    }

    onChange(sortTimeSlots(value + (value ? ', ' : '') + newOnes.join(', ')))
    setNewSlot('')
    toastSuccess(
      newOnes.length === 1
        ? `Added ${timeSlotToFriendly(newOnes[0]!)}`
        : `Added ${newOnes.length} time slots`,
    )
  }, [newSlot, onChange, value])

  const handleCopy = useCallback(() => {
    void navigator.clipboard.writeText(value).then(
      () => toastSuccess('Time slots copied'),
      () => toastError('Could not copy'),
    )
  }, [value])

  const handlePaste = useCallback(async () => {
    try {
      const text = await navigator.clipboard.readText()
      onChange(sortTimeSlots(text.trim()))
      toastSuccess('Time slots pasted')
    } catch {
      toastError('Could not paste')
    }
  }, [onChange])

  const handleClear = useCallback(() => {
    if (!value.trim()) return
    if (!confirm('Clear all time slots?')) return
    onChange('')
    toastSuccess('Time slots cleared')
  }, [onChange, value])

  return (
    <div className='TimeSlots'>
      <ChipBox
        label={label}
        tooltip={tooltip}
        disabled={disabled}
        renderChip={(s) => renderTimeSlot(s)}
        value={value.split(',').filter(Boolean).map((s) => s.trim())}
        setValue={(next) => onChange(next.join(', '))}
      >
        <TextBox
          placeholder='3am, 5:30pm'
          width={addFieldWidth}
          value={newSlot}
          setValue={setNewSlot}
          disabled={disabled}
          onKeyDown={(ev) => {
            if (ev.key === 'Enter') {
              handleAdd()
            } else if (ev.key === 'Escape') {
              setNewSlot('')
            }
          }}
        />
        <IconButton
          icon='plus'
          tooltip='Add time slot'
          disabled={disabled || !newSlot.trim()}
          onClick={handleAdd}
        />
        <IconButton
          icon='copy'
          tooltip='Copy all'
          disabled={disabled}
          onClick={handleCopy}
        />
        <IconButton
          icon='paste'
          tooltip='Paste all'
          disabled={disabled}
          onClick={handlePaste}
        />
        <IconButton
          icon='trash'
          tooltip='Clear all'
          disabled={disabled || !value.trim()}
          onClick={handleClear}
        />
      </ChipBox>
    </div>
  )
}


//--------------------------------------------------------------------------------
function sortTimeSlots(s: string): string {
  return s
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .sort((a, b) => +a - +b)
    .join(', ')
}

//--------------------------------------------------------------------------------
/** Parse a time string to HHMMSS number (e.g. 173000 for 5:30pm). Returns null if invalid. */
function parseTimeSlot(input: string): number | null {
  const s = input.trim().toLowerCase()
  if (!s) return null

  const twelve = s.match(/^(\d{1,2})(?::(\d{1,2}))?\s*(am|pm)$/)
  if (twelve) {
    let h = parseInt(twelve[1]!, 10)
    const m = twelve[2] ? parseInt(twelve[2], 10) : 0
    const pm = twelve[3] === 'pm'
    if (h < 1 || h > 12 || m < 0 || m > 59) return null
    if (pm && h !== 12) h += 12
    if (!pm && h === 12) h = 0
    return h * 10000 + m * 100
  }

  const twentyFour = s.match(/^(\d{1,2}):(\d{2})$/)
  if (twentyFour) {
    const h = parseInt(twentyFour[1]!, 10)
    const m = parseInt(twentyFour[2]!, 10)
    if (h < 0 || h > 23 || m < 0 || m > 59) return null
    return h * 10000 + m * 100
  }

  const four = s.match(/^(\d{2})(\d{2})$/)
  if (four) {
    const h = parseInt(four[1]!, 10)
    const m = parseInt(four[2]!, 10)
    if (h < 0 || h > 23 || m < 0 || m > 59) return null
    return h * 10000 + m * 100
  }

  return null
}

//--------------------------------------------------------------------------------
/** Format HHMMSS number as friendly string for display (e.g. "3am", "5:30pm"). */
function timeSlotToFriendly(hhmmss: number): string {
  const h = Math.floor(hhmmss / 10000)
  const m = Math.floor((hhmmss % 10000) / 100)
  const pm = h >= 12
  const h12 = h === 0 ? 12 : h > 12 ? h - 12 : h
  if (m === 0) return `${h12}${pm ? 'pm' : 'am'}`
  const mm = m < 10 ? `0${m}` : String(m)
  return `${h12}:${mm}${pm ? 'pm' : 'am'}`
}

//--------------------------------------------------------------------------------
function renderTimeSlot(s: string): ReactNode {
  const trimmed = s.trim()
  if (!/^\d+$/.test(trimmed)) return s
  const six = trimmed.length <= 6 ? trimmed.padStart(6, '0') : trimmed
  const parts = six.match(/^(\d{2})(\d{2})(\d{2})$/)
  if (!parts) return s
  const h24 = +parts[1]!
  const minutes = parts[2]
  const pm = h24 >= 12
  const h12 = h24 === 0 ? 12 : h24 > 12 ? h24 - 12 : h24
  const amPm = pm ? 'pm' : 'am'
  if (minutes === '00')
    return <span>{h12}{amPm}</span>
  return <span>{h12}:{minutes}{amPm}</span>
}
